'use client';

import { HpMarketingAdWithCards } from '@pickleballinc/features/src/global/cards/hp-marketing-ad-with-cards';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'next/navigation';

import { PickleballCentralLogo } from '@/modules/players/v3/components/images/pickleball-central-logo';
import { AdvertisementLabel } from '@/modules/players/v3/components/player-pages/dashboard/advertisement-label';
import { DashboardWidgetCardCarousel } from '@/modules/players/v3/components/player-pages/dashboard/dashboard-widget-card-carousel';
import { MyGearGrid } from '@/modules/players/v3/components/player-pages/dashboard/my-gear/my-gear-grid';
import { MyGearSkeleton } from '@/modules/players/v3/components/player-pages/dashboard/my-gear/my-gear-skeleton';
import { PickleballCentralGearCard } from '@/modules/players/v3/components/player-pages/dashboard/my-gear/pickleball-central-gear-card';
import { ShopButton } from '@/modules/players/v3/components/player-pages/dashboard/my-gear/shop-button';
import { MyGearEmptyState } from '@/modules/players/v3/components/player-pages/gear/my-gear-empty-state';
import { CardLink, CardTitle } from '@/modules/players/v3/components/shared/card/card';
import { DashboardWidgetCard } from '@/modules/players/v3/components/shared/dashboard-widget-card/dashboard-widget-card';
import {
  PAGE_EMPTY_MASCOT_SLOT,
  PlayerDashboardEmptyContent,
} from '@/modules/players/v3/components/shared/dashboard-widget-card/player-dashboard-empty-content';
import { PICKLEBALL_CENTRAL_URL } from '@/modules/players/v3/constants/constants';
import { DASHBOARD_ILLUSTRATION } from '@/modules/players/v3/constants/dashboard-illustrations';
import { DEFAULT_PBC_DEALS_LIMIT } from '@/modules/players/v3/constants/gear';
import { useHpMarketingAd } from '@/modules/players/v3/hooks/use-hp-marketing-ad';
import { Gear, PlayerGear } from '@/modules/players/v3/types';
import { PROFILE_QUERY_KEY } from '@/modules/players/v3/utils/my-gear';
import { toWidgetErrorState } from '@/modules/players/v3/utils/widget-error-state';
import { User } from '@/types/players.types';
import { SystemModuleType } from '@/types/types';
import { FEATURE_FLAGS } from '@/utils/helpers/constants';
import { fetchRoute } from '@/utils/helpers/fetchRoute';

interface MyGearProps {
  editPlayer: User;
  isWebview?: boolean;
  showOwnerActions?: boolean;
  showPickleballCentralDeals?: boolean;
  showAddGearCard?: boolean;
}

export const MyGear = ({
  editPlayer,
  isWebview,
  showOwnerActions = true,
  showPickleballCentralDeals = true,
  showAddGearCard,
}: MyGearProps) => {
  const { slug } = useParams();

  const {
    data: playerGear = [],
    isLoading: isLoadingGear,
    isError: isGearError,
    refetch: refetchGear,
  } = useQuery({
    queryKey: [PROFILE_QUERY_KEY, editPlayer.uuid],
    queryFn: async ({ signal }) => {
      const data = await fetchRoute<{ gear: PlayerGear[] }>(
        '/api/v2/players/getPlayerGear',
        { playerUuid: editPlayer.uuid },
        signal
      );
      return data?.gear ?? [];
    },
  });

  const {
    ads: searchCardAds,
    isLoading: isAdLoading,
    hasAd,
  } = useHpMarketingAd(SystemModuleType.PICKLEBALL_CENTRAL_MODULE_TYPE);

  const { data: gearProducts = [], isLoading: isLoadingProducts } = useQuery({
    queryKey: ['dashboard-gear-products'],
    queryFn: async ({ signal }) => {
      const data = await fetchRoute<{ products: Gear[] }>(
        '/api/v2/gear/getGears',
        { limit: DEFAULT_PBC_DEALS_LIMIT },
        signal
      );
      return data?.products ?? [];
    },
  });

  const isLoading = isLoadingGear || isAdLoading || isLoadingProducts;

  return (
    <DashboardWidgetCard
      isLoading={isLoading}
      skeleton={<MyGearSkeleton />}
      header={
        <>
          <div className='flex min-w-0 items-center gap-2'>
            <CardTitle>My gear</CardTitle>
            {!isWebview ? (
              <div className='hidden items-center gap-2 md:flex'>
                <span className='whitespace-nowrap text-sm text-gray-600'>sponsored by</span>
                <PickleballCentralLogo clickable={!isWebview} />
              </div>
            ) : null}
          </div>
          {showOwnerActions && playerGear.length > 0 ? (
            <CardLink href={`/players/${slug}/v3/profile/my-gear`} className='whitespace-nowrap'>
              Manage my gear
            </CardLink>
          ) : null}
        </>
      }
      contentClassName='gap-16 bg-gray-50 pb-6 md:gap-14'
    >
      <div className='flex flex-col gap-4'>
        {isGearError ? (
          <div className='overflow-hidden rounded-2xl border border-gray-200 bg-white'>
            <PlayerDashboardEmptyContent
              {...toWidgetErrorState(
                {
                  visual: {
                    kind: 'mascot',
                    src: DASHBOARD_ILLUSTRATION.MASCOT_RUNNING,
                    position: 'bottom-left',
                  },
                  mascotSlotClassName: PAGE_EMPTY_MASCOT_SLOT,
                },
                'Couldn’t load your gear',
                refetchGear
              )}
              className='min-h-[220px] justify-center py-4'
            />
          </div>
        ) : playerGear.length > 0 || (showAddGearCard ?? showOwnerActions) ? (
          <MyGearGrid
            editPlayer={editPlayer}
            playerGear={playerGear}
            showOwnerActions={showOwnerActions}
            showAddGearCard={showAddGearCard}
          />
        ) : (
          <MyGearEmptyState
            ctaHref={PICKLEBALL_CENTRAL_URL}
            title='No gear added yet'
            description='Curious what gear players are using? Pickleball Central has you covered.'
            ctaLabel='Explore Pickleball Central'
            ctaExternal
          />
        )}
      </div>
      {showPickleballCentralDeals ? (
        <div className='flex flex-col gap-4'>
          <h3 className='text-lg font-semibold text-gray-900'>Pickleball Central deals for you</h3>
          {hasAd ? (
            <div className='flex flex-col gap-1'>
              <AdvertisementLabel />
              <HpMarketingAdWithCards
                ads={searchCardAds}
                moduleFor={SystemModuleType.PICKLEBALL_CENTRAL_MODULE_TYPE}
                useV2Cards={FEATURE_FLAGS.ENABLE_NEW_CARDS}
                desktopLayout='flow'
              />
            </div>
          ) : null}
          {gearProducts.length === 0 ? (
            <div className='overflow-hidden rounded-2xl border border-gray-200 bg-white'>
              <PlayerDashboardEmptyContent
                className='min-h-[220px] justify-center py-4'
                title='New deals are on the way'
                description='Check back soon — fresh Pickleball Central offers land here regularly.'
                visual={{
                  kind: 'mascot',
                  src: DASHBOARD_ILLUSTRATION.MASCOT_WAVING_BAG,
                  position: 'bottom-left',
                }}
                mascotSlotClassName={PAGE_EMPTY_MASCOT_SLOT}
              />
            </div>
          ) : (
            <>
              <div className='md:hidden'>
                <DashboardWidgetCardCarousel itemsPerPage={1}>
                  {gearProducts.map((gear) => (
                    <PickleballCentralGearCard key={gear.id} gear={gear} isWebview={isWebview} />
                  ))}
                </DashboardWidgetCardCarousel>
              </div>

              <div className='hidden md:grid gap-4 grid-cols-3 lg:grid-cols-4'>
                {gearProducts.map((gear) => (
                  <PickleballCentralGearCard key={gear.id} gear={gear} isWebview={isWebview} />
                ))}
              </div>

              {!isWebview ? (
                <div className='flex justify-center pt-6'>
                  <ShopButton className='w-[140px]'>
                    <a
                      href={PICKLEBALL_CENTRAL_URL}
                      target='_blank'
                      rel='noopener noreferrer'
                      aria-label='Shop all on Pickleball Central (opens in a new tab)'
                    >
                      Shop all
                    </a>
                  </ShopButton>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </DashboardWidgetCard>
  );
};
